import { Field } from 'formik';
import type { FormikHelpers } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess, hidesPurchasePrices } from 'utils/helpers/access/hasUserAccess';
import FormArticleInput from 'components/forms/FormArticleInput';
import InputPriceRate from 'components/others/InputPriceRate';
import DimensionSelect from '../../../Products/DimensionSelect';
import CatalogCategorySelect from '../../../CatalogCategory/CatalogCategorySelect';
import type { SelectOption, ServiceEditType } from '../../types';
import ServiceMontageName from './ServiceMontageName';

interface ServiceFormGeneralProps {
    isSubmitting: boolean;
    setFieldValue: FormikHelpers<ServiceEditType>['setFieldValue'];
    values: ServiceEditType;
    service: ServiceEditType;
}

const ServiceFormGeneral = ({ setFieldValue, values, service }: ServiceFormGeneralProps) => {
    const { auth } = useAuth();
    const canEdit = hasUserAccess(auth?.user, 'prodEdit');
    const hidePurchase = hidesPurchasePrices(auth?.user);
    const { t } = useTranslation(['Services', 'common']);
    const refInputName = useRef<HTMLInputElement>(null);
    const isCreate = Number(service?.ID || values?.ID || 0) === 0;
    const [montageMode, setMontageMode] = useState(false);

    useEffect(() => {
        if (montageMode) return;
        setTimeout(() => {
            refInputName.current?.focus();
        }, 100);
    }, [isCreate, service.ID, montageMode]);

    const handleMontageToggle = (checked: boolean) => {
        setMontageMode(checked);
        if (!checked) return;
        setFieldValue('servName', '');
    };

    return (
        <>
            <FormArticleInput
                label={t('fields.article')}
                name="servArticle"
                disabled={!isCreate || !canEdit}
                canGenerate={isCreate && canEdit}
                onChange={(article) => setFieldValue('servArticle', article)}
            />

            <Form.Group className="mb-3">
                <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                    <label className="form-label text-muted mb-0">{t('fields.name')}</label>
                    {isCreate && canEdit ? (
                        <div className="form-check form-switch mb-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="service-montage-mode"
                                checked={montageMode}
                                onChange={(e) => handleMontageToggle(Boolean(e.target.checked))}
                            />
                            <label className="form-check-label small text-muted" htmlFor="service-montage-mode">
                                {t('fields.montageMode')}
                            </label>
                        </div>
                    ) : null}
                </div>

                {montageMode && isCreate ? (
                    <>
                        <ServiceMontageName
                            disabled={!canEdit}
                            onNameChange={(name) => setFieldValue('servName', name)}
                        />
                        <Field
                            name="servName"
                            disabled={!canEdit}
                            className="form-control"
                            placeholder={t('fields.montageNamePlaceholder')}
                        />
                    </>
                ) : (
                    <Field
                        innerRef={refInputName}
                        name="servName"
                        disabled={!canEdit}
                        className="form-control"
                    />
                )}
            </Form.Group>

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.category')}</label>
                <CatalogCategorySelect
                    kind="service"
                    value={values.categoryId}
                    disabled={!canEdit}
                    onChange={(opt) => setFieldValue('categoryId', opt?.value || 0)}
                />
            </Form.Group>

            {hidePurchase ? null : (
            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.price')}</label>
                <InputPriceRate
                    value={values.servDefPrice || 0}
                    onValueChange={(val) => {
                        setFieldValue('servDefPrice', val.floatValue);
                    }}
                />
            </Form.Group>
            )}

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.priceSale')}</label>
                <InputPriceRate
                    value={values.servDefPriceSale || 0}
                    onValueChange={(val) => {
                        setFieldValue('servDefPriceSale', val.floatValue);
                    }}
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.dimension')}</label>
                <DimensionSelect
                    required={false}
                    value={values.dimensionId}
                    onChange={(opt: SelectOption | null) => {
                        setFieldValue('dimensionId', opt?.value);
                    }}
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.description')}</label>
                <Field
                    name="servDescription"
                    as="textarea"
                    rows="4"
                    disabled={!canEdit}
                    className="form-control"
                />
            </Form.Group>
        </>
    );
};

export default ServiceFormGeneral;
